import { useState, useEffect } from 'react';
import AOS from 'aos';
import { lockScroll, unlockScroll, scrollToY } from '../smoothScroll';
import './navbar.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const menuWasOpen = isMenuOpen;
    setIsMenuOpen(false);
    document.body.classList.add('nav-scrolling');

    const scrollToTarget = () => {
      if (targetId === '#top') {
        scrollToY(0);
      } else {
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          const scrollMargin = parseInt(getComputedStyle(targetElement).scrollMarginTop) || 0;
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          const target = Math.min(targetElement.offsetTop - scrollMargin, maxScroll);
          scrollToY(target);
        }
      }
      setTimeout(() => {
        document.body.classList.remove('nav-scrolling');
        AOS.refresh();
      }, 1200);
    };

    if (menuWasOpen) {
      setTimeout(scrollToTarget, 300);
    } else {
      scrollToTarget();
    }
  };

  useEffect(() => {
    if (!isMenuOpen) return;
    lockScroll();
    return unlockScroll;
  }, [isMenuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`navbar ${isMenuOpen ? 'active' : ''} ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-logo">
            <a href="#top" onClick={(e) => handleNavClick(e, '#top')}>
              <img src="/logo.png" alt="Hair by Katheryne" />
            </a>
          </div>

          {/* Desktop navigation links — absolutely centred */}
          <div className="navbar-links">
            <a href="#top"      onClick={(e) => handleNavClick(e, '#top')}>Home</a>
            <a href="#services" onClick={(e) => handleNavClick(e, '#services')}>Services</a>
            <a href="#gallery"  onClick={(e) => handleNavClick(e, '#gallery')}>Gallery</a>
            <a href="#hours"    onClick={(e) => handleNavClick(e, '#hours')}>Hours</a>
          </div>

          {/* Right: phone + book */}
          <div className="navbar-right">
            <a href="sms:+14016225651" className="navbar-phone">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M11.953 2.25c-2.317 0-4.118 0-5.52.15c-1.418.153-2.541.47-3.437 1.186c-.92.736-1.35 1.693-1.553 2.9c-.193 1.152-.193 2.618-.193 4.446v.183c0 1.782 0 3.015.2 3.934c.108.495.278.925.545 1.323c.264.392.6.722 1.001 1.042c.631.505 1.375.81 2.254 1V21a.75.75 0 0 0 1.123.65c.586-.335 1.105-.7 1.58-1.044l.304-.221a22 22 0 0 1 1.036-.73c.844-.548 1.65-.905 2.707-.905h.047c2.317 0 4.118 0 5.52-.15c1.418-.153 2.541-.47 3.437-1.186c.4-.32.737-.65 1-1.042c.268-.398.438-.828.546-1.323c.2-.919.2-2.152.2-3.934v-.183c0-1.828 0-3.294-.193-4.445c-.203-1.208-.633-2.165-1.553-2.901c-.896-.717-2.019-1.033-3.437-1.185c-1.402-.151-3.203-.151-5.52-.151z"/>
              </svg>
              <span>+1 (401) 622 5651</span>
            </a>

            <a
              href="https://www.vagaro.com/hairbykatheryne"
              target="_blank"
              rel="noreferrer"
              className="navbar-book"
            >
              Book Now
            </a>
          </div>

          {/* Hamburger */}
          <button
            className={`hamburger ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="line line1" />
            <span className="line line2" />
          </button>
        </div>
      </nav>

      {/* Mobile full-screen menu — outside <nav> so backdrop-filter doesn't trap fixed positioning */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-image">
          <img src="/circle.webp" alt="" aria-hidden="true" />
        </div>

        <nav className="mobile-menu-nav">
          <a href="#top"       onClick={(e) => handleNavClick(e, '#top')}>Home</a>
          <a href="#services"  onClick={(e) => handleNavClick(e, '#services')}>Services</a>
          <a href="#gallery"   onClick={(e) => handleNavClick(e, '#gallery')}>Gallery</a>
          <a href="#hours"     onClick={(e) => handleNavClick(e, '#hours')}>Hours</a>
          <a href="https://www.vagaro.com/hairbykatheryne" target="_blank" rel="noreferrer" className="mobile-book">Book Now</a>
        </nav>

        <div className="mobile-menu-footer">
          <a href="sms:+14016225651" className="mobile-footer-link" onClick={toggleMenu}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M11.953 2.25c-2.317 0-4.118 0-5.52.15c-1.418.153-2.541.47-3.437 1.186c-.92.736-1.35 1.693-1.553 2.9c-.193 1.152-.193 2.618-.193 4.446v.183c0 1.782 0 3.015.2 3.934c.108.495.278.925.545 1.323c.264.392.6.722 1.001 1.042c.631.505 1.375.81 2.254 1V21a.75.75 0 0 0 1.123.65c.586-.335 1.105-.7 1.58-1.044l.304-.221a22 22 0 0 1 1.036-.73c.844-.548 1.65-.905 2.707-.905h.047c2.317 0 4.118 0 5.52-.15c1.418-.153 2.541-.47 3.437-1.186c.4-.32.737-.65 1-1.042c.268-.398.438-.828.546-1.323c.2-.919.2-2.152.2-3.934v-.183c0-1.828 0-3.294-.193-4.445c-.203-1.208-.633-2.165-1.553-2.901c-.896-.717-2.019-1.033-3.437-1.185c-1.402-.151-3.203-.151-5.52-.151z"/>
            </svg>
            Reach Out
          </a>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
      />
    </>
  );
};

export default Navbar;
